/**
 * The Pipeline report's layout, on screen.
 *
 * Print, PDF and Excel render the Backshop pipeline report from ONE server definition
 * (backend/app/reporting/pipeline_backshop.py): 26 columns in Backshop's order, each under
 * Backshop's own header, with the deals grouped under their stage. The server SERVES that
 * definition (`GET /reports/pipeline-export-settings` → `backshopLayout`); this module carries
 * no copy of it. The Pipeline page and the Command Center draw the grid from what the server
 * sent, so what the desk reads on screen is what Print prints — a screen that typed its own
 * list of columns drifted from the document (it did: the page showed 14 working columns while
 * Print drew Backshop's 26).
 */
import type { Loan } from '@/types'
import type { ColumnId } from './types'

/** The definition as the server serves it. */
export type PipelineReportLayout = {
  id: string
  title: string
  columns: Array<{ id: ColumnId; header: string }>
  /** Stage header rows, in print order. `value` is what a deal's stage reads. */
  stages: Array<{ value: string; header: string }>
}

/** A grid showing the report's layout: its headers and its stage groups. */
export type ScreenLayout = {
  headers: ReadonlyMap<ColumnId, string>
  stages: PipelineReportLayout['stages']
}

/** One row of the grid: a stage header, or a deal. */
export type GridRow =
  | { kind: 'group'; key: string; label: string; count: number }
  | { kind: 'loan'; loan: Loan }

/** The ids of the layout's columns, in the report's order. */
export function layoutColumnIds(layout: PipelineReportLayout): ColumnId[] {
  return layout.columns.map((column) => column.id)
}

/**
 * The layout to draw `columns` in, or null when `columns` is not the report's layout.
 *
 * The grid shows the report's layout only when the columns it was handed ARE the report's
 * columns, in the report's order. A saved view with a column set of its own (the dead-deal
 * comparison, the funded book) is not the report and renders as the plain grid it always was.
 */
export function screenLayoutFor(
  layout: PipelineReportLayout | null | undefined,
  columns: readonly ColumnId[],
): ScreenLayout | null {
  if (!layout || layout.columns.length === 0) return null
  const ids = layoutColumnIds(layout)
  if (ids.length !== columns.length || ids.some((id, index) => id !== columns[index])) return null
  return {
    headers: new Map(layout.columns.map((column) => [column.id, column.header] as const)),
    stages: layout.stages,
  }
}

/**
 * The deals under their stage headers, stages in print order, deals in the order given
 * (the caller's sort applies within each stage).
 *
 * A stage the report has no header for still shows, last, under its own name: a deal is
 * never dropped from the screen because its stage is unfamiliar. A stage with no deals has no
 * header — nothing prints an empty band.
 */
export function groupByStage(loans: readonly Loan[], stages: PipelineReportLayout['stages']): GridRow[] {
  const known = new Map(stages.map((stage, index) => [stage.value, index] as const))
  const buckets = new Map<string, Loan[]>()
  for (const loan of loans) {
    const key = String(loan.stage ?? '')
    const bucket = buckets.get(key)
    if (bucket) bucket.push(loan)
    else buckets.set(key, [loan])
  }
  const ordered = [...buckets.entries()].sort(([a], [b]) => {
    const left = known.get(a) ?? stages.length
    const right = known.get(b) ?? stages.length
    return left !== right ? left - right : a.localeCompare(b)
  })
  const headerOf = new Map(stages.map((stage) => [stage.value, stage.header] as const))
  const rows: GridRow[] = []
  for (const [stage, group] of ordered) {
    rows.push({ kind: 'group', key: stage || 'none', label: headerOf.get(stage) ?? (stage || 'No stage'), count: group.length })
    for (const loan of group) rows.push({ kind: 'loan', loan })
  }
  return rows
}

/** The order the stage groups read on screen (T-63, 2 Nov 2026): the report's own
 *  (Funded first, as Backshop prints it) or reversed, Triage first, for an originator. */
export type StageOrder = 'funded-first' | 'triage-first'

/** The layout with its stage groups in `order`. Only the screen reads this; Print keeps the report's. */
export function withStageOrder(layout: ScreenLayout | null, order: StageOrder): ScreenLayout | null {
  if (!layout || order === 'funded-first') return layout
  return { ...layout, stages: [...layout.stages].reverse() }
}

/** The rows of a plain grid: every deal, no headers. */
export function plainRows(loans: readonly Loan[]): GridRow[] {
  return loans.map((loan) => ({ kind: 'loan', loan }))
}
